import React from 'react';
import {SceneData, SceneEntity, SceneEvents} from './types.ts';
import {EntityManipulationMethods} from '../../types.ts';
import {Button, FormGroup, Input, Label} from 'reactstrap';
import Icon from '../shared/Icon.tsx';
import {PlusIcon, TrashIcon} from 'lucide-react';
import SpatialCoordinateEditor from './SpatialCoordinateEditor.tsx';
import MaterialEditor from './MaterialEditor.tsx';
import SphereEditor from './SphereEditor.tsx';
import BoxEditor from './BoxEditor.tsx';
import TorusEditor from './TorusEditor.tsx';
import CylinderEditor from './CylinderEditor.tsx';
import PlaneEditor from './PlaneEditor.tsx';
import DiscEditor from './DiscEditor.tsx';
import IcosphereEditor from './IcosphereEditor.tsx';
import LightEditor from './LightEditor.tsx';
import TorusKnotEditor from './TorusKnotEditor.tsx';
import GroundEditor from './GroundEditor.tsx';
import LinesEditor from './LinesEditor.tsx';
import DashedLinesEditor from './DashedLinesEditor.tsx';
import TubeEditor from './TubeEditor.tsx';
import ExtrudeShapeEditor from './ExtrudeShapeEditor.tsx';
import LatheEditor from './LatheEditor.tsx';
import TextEditor from './TextEditor.tsx';
import AnimationEditor from './AnimationEditor.tsx';
import useDraft from './useDraft.ts';
import sceneEntityLabel from './sceneEntityLabel.ts';
import {isInside, parentGroupId} from './sceneTree.ts';

interface SceneEntityControlProps {
    entity: SceneEntity;
    elementId: string;
    sceneData: SceneData;
    manipulation: EntityManipulationMethods;
}

const lightTypes = [
    'pointLight',
    'directionalLight',
    'spotLight',
    'hemisphericLight'
];

// Meshes that take a material (models, lines, text and groups don't).
const materialTypes = [
    'sphere',
    'box',
    'torus',
    'cylinder',
    'plane',
    'disc',
    'icosphere',
    'torusKnot',
    'ground',
    'tube',
    'extrudeShape',
    'lathe'
];

const SceneEntityControl: React.FC<SceneEntityControlProps> = ({
    entity,
    elementId,
    sceneData,
    manipulation
}) => {
    const {mergeData, removeElement} = manipulation;

    const handleChange = (property: string, value: unknown) => {
        mergeData(elementId, {[property]: value}, SceneEvents.EntityModify);
    };

    const [name, setName] = useDraft(entity.name ?? '');

    const commitName = () => {
        const trimmed = name.trim();
        setName(trimmed);
        if (trimmed !== (entity.name ?? '')) {
            handleChange('name', trimmed || null);
        }
    };

    // Any group but this one and those inside it.
    const groupChoices = Object.entries(sceneData).filter(
        ([groupId, group]) =>
            group.type === 'group' && !isInside(sceneData, groupId, elementId)
    );

    // An extrudeShape's numeric `rotation` is an old row's twist.
    const legacyTwist =
        typeof (entity.rotation as unknown) === 'number'
            ? (entity.rotation as unknown as number)
            : undefined;
    const rotation =
        entity.rotation && typeof entity.rotation === 'object'
            ? entity.rotation
            : undefined;

    // An old row's numeric rotation is cleared once the twist has its own
    // property, so the two can't disagree.
    const handleTwistChange = (twist: number | null) =>
        mergeData(
            elementId,
            legacyTwist === undefined ? {twist} : {twist, rotation: null},
            SceneEvents.EntityModify
        );

    const remove = () => {
        if (entity.type === 'group') {
            // Its children move up to the group's own group, or the top level.
            const newParent = parentGroupId(sceneData, elementId);
            Object.entries(sceneData).forEach(([childId, child]) => {
                if (childId !== elementId && child.parent === elementId) {
                    mergeData(
                        childId,
                        {parent: childId === newParent ? null : newParent},
                        SceneEvents.EntityModify
                    );
                }
            });
        }

        removeElement(elementId, SceneEvents.EntityDelete);
    };

    return (
        <div className="p-2">
            <div>
                <b>Type:</b> {entity.type}
            </div>

            <FormGroup>
                <Label>Name:</Label>
                <Input
                    type="text"
                    value={name}
                    placeholder={sceneEntityLabel({...entity, name: null})}
                    onChange={e => setName(e.target.value)}
                    onBlur={commitName}
                    onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                />
            </FormGroup>

            <FormGroup>
                <Label>Group:</Label>
                <Input
                    type="select"
                    value={parentGroupId(sceneData, elementId) ?? ''}
                    onChange={e =>
                        handleChange('parent', e.target.value || null)
                    }
                >
                    <option value="">None</option>
                    {groupChoices.map(([groupId, group]) => (
                        <option key={groupId} value={groupId}>
                            {sceneEntityLabel(group)}
                        </option>
                    ))}
                </Input>
            </FormGroup>

            {entity.type == 'model' && (
                <div>
                    <b>Model:</b> {entity.url}
                </div>
            )}

            {entity.position && (
                <SpatialCoordinateEditor
                    label={<b>Position</b>}
                    coordinate={entity.position}
                    onChange={coordinate =>
                        handleChange('position', coordinate)
                    }
                />
            )}

            {rotation && (
                <SpatialCoordinateEditor
                    label={<b>Rotation</b>}
                    coordinate={rotation}
                    onChange={coordinate =>
                        handleChange('rotation', coordinate)
                    }
                />
            )}

            {entity.scaling && (
                <SpatialCoordinateEditor
                    label={<b>Scaling</b>}
                    coordinate={entity.scaling}
                    onChange={coordinate => handleChange('scaling', coordinate)}
                />
            )}

            {!lightTypes.includes(entity.type) && (
                <AnimationEditor
                    animation={entity.animation}
                    onChange={value => handleChange('animation', value)}
                />
            )}

            {entity.material && (
                <MaterialEditor
                    material={entity.material}
                    onChange={value => handleChange('material', value)}
                />
            )}

            {!entity.material && materialTypes.includes(entity.type) && (
                <div className="p-2">
                    <Button
                        color="secondary"
                        size="sm"
                        onClick={() =>
                            handleChange('material', {color: '#ffffff'})
                        }
                    >
                        <Icon icon={PlusIcon} /> Add material
                    </Button>
                </div>
            )}

            {entity.type === 'sphere' && (
                <SphereEditor
                    diameter={entity.diameter}
                    segments={entity.segments}
                    onChange={value => handleChange('diameter', value)}
                    onSegmentsChange={value => handleChange('segments', value)}
                />
            )}

            {entity.type === 'box' && (
                <BoxEditor
                    size={entity.size}
                    width={entity.width}
                    height={entity.height}
                    depth={entity.depth}
                    onChange={value => handleChange('size', value)}
                    onWidthChange={value => handleChange('width', value)}
                    onHeightChange={value => handleChange('height', value)}
                    onDepthChange={value => handleChange('depth', value)}
                />
            )}

            {entity.type === 'torus' && (
                <TorusEditor
                    diameter={entity.diameter}
                    thickness={entity.thickness}
                    onDiameterChange={value => handleChange('diameter', value)}
                    onThicknessChange={value =>
                        handleChange('thickness', value)
                    }
                />
            )}

            {entity.type === 'cylinder' && (
                <CylinderEditor
                    height={entity.height}
                    diameter={entity.diameter}
                    diameterTop={entity.diameterTop}
                    diameterBottom={entity.diameterBottom}
                    tessellation={entity.tessellation}
                    onHeightChange={value => handleChange('height', value)}
                    onDiameterChange={value => handleChange('diameter', value)}
                    onDiameterTopChange={value =>
                        handleChange('diameterTop', value)
                    }
                    onDiameterBottomChange={value =>
                        handleChange('diameterBottom', value)
                    }
                    onTessellationChange={value =>
                        handleChange('tessellation', value)
                    }
                />
            )}

            {entity.type === 'plane' && (
                <PlaneEditor
                    width={entity.width}
                    height={entity.height}
                    doubleSided={entity.doubleSided}
                    onWidthChange={value => handleChange('width', value)}
                    onHeightChange={value => handleChange('height', value)}
                    onDoubleSidedChange={value =>
                        handleChange('doubleSided', value)
                    }
                />
            )}

            {entity.type === 'disc' && (
                <DiscEditor
                    radius={entity.radius}
                    tessellation={entity.tessellation}
                    onChange={value => handleChange('radius', value)}
                    onTessellationChange={value =>
                        handleChange('tessellation', value)
                    }
                />
            )}

            {entity.type === 'icosphere' && (
                <IcosphereEditor
                    radius={entity.radius}
                    subdivisions={entity.subdivisions}
                    onRadiusChange={value => handleChange('radius', value)}
                    onSubdivisionsChange={value =>
                        handleChange('subdivisions', value)
                    }
                />
            )}

            {(entity.type === 'pointLight' ||
                entity.type === 'directionalLight' ||
                entity.type === 'spotLight' ||
                entity.type === 'hemisphericLight') && (
                <LightEditor
                    light={entity}
                    onIntensityChange={value =>
                        handleChange('intensity', value)
                    }
                    onDirectionChange={value =>
                        handleChange('direction', value)
                    }
                    onAngleChange={value => handleChange('angle', value)}
                    onExponentChange={value => handleChange('exponent', value)}
                    onColorChange={value => handleChange('color', value)}
                    onGroundColorChange={value =>
                        handleChange('groundColor', value)
                    }
                />
            )}

            {entity.type === 'torusKnot' && (
                <TorusKnotEditor
                    radius={entity.radius}
                    tube={entity.tube}
                    radialSegments={entity.radialSegments}
                    tubularSegments={entity.tubularSegments}
                    p={entity.p}
                    q={entity.q}
                    onRadiusChange={value => handleChange('radius', value)}
                    onTubeChange={value => handleChange('tube', value)}
                    onRadialSegmentsChange={value =>
                        handleChange('radialSegments', value)
                    }
                    onTubularSegmentsChange={value =>
                        handleChange('tubularSegments', value)
                    }
                    onPChange={value => handleChange('p', value)}
                    onQChange={value => handleChange('q', value)}
                />
            )}

            {entity.type === 'ground' && (
                <GroundEditor
                    width={entity.width}
                    height={entity.height}
                    subdivisions={entity.subdivisions}
                    heightMap={entity.heightMap}
                    maxHeight={entity.maxHeight}
                    onWidthChange={value => handleChange('width', value)}
                    onHeightChange={value => handleChange('height', value)}
                    onSubdivisionsChange={value =>
                        handleChange('subdivisions', value)
                    }
                    onHeightMapChange={value =>
                        handleChange('heightMap', value)
                    }
                    onMaxHeightChange={value =>
                        handleChange('maxHeight', value)
                    }
                />
            )}

            {entity.type === 'lines' && (
                <LinesEditor
                    points={entity.points}
                    colors={entity.colors}
                    onPointsChange={value => handleChange('points', value)}
                    onColorsChange={value => handleChange('colors', value)}
                />
            )}

            {entity.type === 'dashedLines' && (
                <DashedLinesEditor
                    points={entity.points}
                    dashSize={entity.dashSize}
                    gapSize={entity.gapSize}
                    dashNb={entity.dashNb}
                    colors={entity.colors}
                    onPointsChange={value => handleChange('points', value)}
                    onDashSizeChange={value => handleChange('dashSize', value)}
                    onGapSizeChange={value => handleChange('gapSize', value)}
                    onDashNbChange={value => handleChange('dashNb', value)}
                    onColorsChange={value => handleChange('colors', value)}
                />
            )}

            {entity.type === 'tube' && (
                <TubeEditor
                    path={entity.path}
                    radius={entity.radius}
                    tessellation={entity.tessellation}
                    cap={entity.cap}
                    onPathChange={value => handleChange('path', value)}
                    onRadiusChange={value => handleChange('radius', value)}
                    onTessellationChange={value =>
                        handleChange('tessellation', value)
                    }
                    onCapChange={value => handleChange('cap', value)}
                />
            )}

            {entity.type === 'extrudeShape' && (
                <ExtrudeShapeEditor
                    shape={entity.shape}
                    path={entity.path}
                    scale={entity.scale}
                    twist={entity.twist ?? legacyTwist}
                    cap={entity.cap}
                    onShapeChange={value => handleChange('shape', value)}
                    onPathChange={value => handleChange('path', value)}
                    onScaleChange={value => handleChange('scale', value)}
                    onTwistChange={handleTwistChange}
                    onCapChange={value => handleChange('cap', value)}
                />
            )}

            {entity.type === 'lathe' && (
                <LatheEditor
                    shape={entity.shape}
                    segments={entity.segments}
                    cap={entity.cap}
                    onShapeChange={value => handleChange('shape', value)}
                    onSegmentsChange={value => handleChange('segments', value)}
                    onCapChange={value => handleChange('cap', value)}
                />
            )}

            {entity.type === 'text' && (
                <TextEditor
                    text={entity.text}
                    color={entity.color}
                    height={entity.height}
                    billboard={entity.billboard}
                    background={entity.background}
                    onTextChange={value => handleChange('text', value)}
                    onColorChange={value => handleChange('color', value)}
                    onHeightChange={value => handleChange('height', value)}
                    onBillboardChange={value =>
                        handleChange('billboard', value)
                    }
                    onBackgroundChange={value =>
                        handleChange('background', value)
                    }
                />
            )}

            <div className="d-flex justify-content-end">
                <Button color="danger" size="sm" onClick={remove}>
                    <Icon icon={TrashIcon} />
                </Button>
            </div>
        </div>
    );
};

export default SceneEntityControl;
